import { useEffect, useMemo, useRef } from 'react';

import { themeObjectToString, useThemeObserver } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { getSavedThemeLocal } from '@confluence/theming/entry-points/getSavedThemeLocal.ts';
import { useThemingState } from '@confluence/theming/entry-points/useThemingState.ts';
import {
	setupThemeInsideIframe,
	applyThemeInsideIframe,
} from '@confluence/theming-utils/entry-points/initializeIframeTheming.ts';

import type { WhiteboardThemingService } from './services/whiteboard-theming-service.ts';
import { whiteboardThemingService } from './services/whiteboard-theming-service.ts';

export interface UseThemingService {
	themingService: WhiteboardThemingService;
	initialThemeSerialized?: string;
}

export const useThemingService = (): UseThemingService => {
	const appliedTheme = useThemeObserver();
	const [{ isInitialized: themingInitialized }] = useThemingState();

	const effectiveThemeSerialized = useMemo(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR);
		const theme = isSSR || !themingInitialized ? getSavedThemeLocal() : appliedTheme;
		return theme ? themeObjectToString(theme) : undefined;
	}, [themingInitialized, appliedTheme]);

	// Value should always reflect the originally calculated value
	const initialThemeSerialized = useRef(effectiveThemeSerialized);

	useEffect(() => {
		return () => {
			whiteboardThemingService.dispose();
		};
	}, []);

	useEffect(() => {
		if (appliedTheme) {
			whiteboardThemingService.updateTheme(appliedTheme);
		}
	}, [appliedTheme]);

	// When running inside an iframe (e.g. native embed whiteboard or database),
	// listen for theme changes from the parent page and apply them locally.
	// This bridges the gap where the parent's theme mutation is not visible to
	// `useThemeObserver` running inside this iframe's document.
	// Once applied, `useThemeObserver` above will detect the change and
	// propagate it via `whiteboardThemingService.updateTheme()`.
	useEffect(() => {
		if (!fg('confluence_pass_theme_change_to_iframes')) {
			return;
		}

		if (window === window.parent) {
			return;
		}

		try {
			setupThemeInsideIframe((iframeThemeInfo) => {
				try {
					applyThemeInsideIframe(iframeThemeInfo, document);
				} catch {
					// applyThemeInsideIframe manipulates DOM — if it fails the
					// iframe keeps its current theme which is an acceptable fallback.
				}
			});
		} catch {
			// Expected in cross-origin iframes where window.parent properties
			// are inaccessible. Theme sync is not possible in that case and
			// the iframe will keep its initial theme from the URL query param.
		}
	}, []);

	return {
		themingService: whiteboardThemingService,
		initialThemeSerialized: initialThemeSerialized.current,
	};
};
